import { type ReactNode, useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { cn } from '../../lib/utils.js'
import {
  getStoredTenantSlug,
  getTokenPayload,
  effectiveRoles,
  visibleSubItems,
} from '../../lib/auth.js'
import { config, platform, i18n } from '../../lib/api.js'
import { LocationBanner } from '../location/LocationBanner.js'
import { ImpersonationBanner, focusPageH1 } from '../impersonation/ImpersonationBanner.js'
import { ImpersonationAnnouncer } from '../impersonation/ImpersonationAnnouncer.js'
import {
  StartImpersonationModal,
  type StartTarget,
} from '../impersonation/StartImpersonationModal.js'
import { ExpiredSessionNotice } from '../impersonation/ExpiredSessionNotice.js'
import { flushPendingFocusPageH1 } from '../../lib/impersonation-a11y.js'
import { logoutSession } from '../../lib/session.js'
import { confirmLeaveImpersonation, needsImpersonationStart } from '../../lib/impersonation-nav.js'
import type { Tenant } from '../../lib/api.js'
import { Select, Toaster, Spinner, toast } from '../ui/index.js'
import { CreateTenantModal } from '../platform/CreateTenantModal.js'
import { useI18n } from '../../lib/i18n.js'
import {
  SUB_ITEMS,
  BuildingIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CollapseIcon,
  ExpandIcon,
  LogoutIcon,
  UsersIcon,
} from './nav.js'

const SIDEBAR_COLLAPSED_KEY = 'sr_sidebar_collapsed'

export function Shell({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { t, lang, setLang } = useI18n()
  const { data: catalogLocales } = useQuery({
    queryKey: ['catalog-locales'],
    queryFn: i18n.locales,
  })
  const localeOptions = (catalogLocales ?? []).map((l) => ({ value: l.code, label: l.native }))
  const [sidebarOpen, setSidebarOpen] = useState(false)
  // Colapsada no desktop/tablet: kanban ocupa a tela inteira. Persistida.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
    } catch {
      return false
    }
  })

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0')
      } catch {
        /* storage indisponível — só em memória */
      }
      return next
    })
  }

  const userRoles = effectiveRoles(getTokenPayload())
  const isPlatformOwner = userRoles.includes('platform_owner')

  // Extrair slug ativo do path /r/:slug/
  const activeSlugMatch = location.pathname.match(/^\/r\/([^/]+)/)
  const activeSlug = activeSlugMatch?.[1] ?? null

  // Lista de restaurantes
  const [restaurants, setRestaurants] = useState<Tenant[]>([])
  const [loadingRestaurants, setLoadingRestaurants] = useState(false)

  // Expand state: qual restaurant está expandido
  const [expandedSlugs, setExpandedSlugs] = useState<Set<string>>(() => {
    const s = new Set<string>()
    if (activeSlug) s.add(activeSlug)
    return s
  })

  // Modal de criar restaurante (#418): extraído para components/platform —
  // o Shell só monta e recarrega a lista em onCreated
  const [createModalOpen, setCreateModalOpen] = useState(false)

  // #430 — sessão da plataforma: modal de entrada
  const [startTarget, setStartTarget] = useState<StartTarget | null>(null)
  const [startReturnFocus, setStartReturnFocus] = useState<HTMLElement | null>(null)

  useEffect(() => {
    loadRestaurants()
  }, [])

  // Quando o slug ativo muda, garantir que está expandido
  useEffect(() => {
    if (activeSlug) {
      setExpandedSlugs((prev) => {
        if (prev.has(activeSlug)) return prev
        const next = new Set(prev)
        next.add(activeSlug)
        return next
      })
    }
  }, [activeSlug])

  // Troca de restaurante pela sidebar: as queries não carregam o slug na
  // chave — sem isto, "Início do X → Início do Y" mantinha os dados do X.
  // Remontamos a página (key no <main>) e descartamos o cache ao trocar.
  const lastSlug = useRef(activeSlug)
  useEffect(() => {
    if (lastSlug.current !== activeSlug) {
      lastSlug.current = activeSlug
      queryClient.removeQueries()
    }
  }, [activeSlug, queryClient])

  // #430/#434: foco no h1 DEPOIS da rota nova montar (não em queueMicrotask
  // logo após navigate — esse ainda vê o h1 antigo que some com o remount).
  useEffect(() => {
    flushPendingFocusPageH1()
  }, [location.pathname])

  async function loadRestaurants() {
    setLoadingRestaurants(true)
    try {
      if (isPlatformOwner) {
        const data = await platform.listTenants()
        setRestaurants(data)
      } else if (userRoles.some((r) => ['kitchen', 'waiter', 'manager'].includes(r))) {
        // Cozinha/garçom não acessam /admin/config — entrada mínima pelo slug
        // #319: o restaurante da URL (validado pela sessão) antes do guardado
        const storedSlug = activeSlug ?? getStoredTenantSlug()
        if (storedSlug && storedSlug !== 'platform') {
          setRestaurants([
            {
              id: storedSlug,
              slug: storedSlug,
              name: storedSlug,
              status: 'active',
              country_code: '',
              locale: '',
              custom_domain: null,
              created_at: 0,
              updated_at: 0,
            },
          ])
        }
      } else {
        // restaurant_owner: carrega o seu próprio restaurante
        // #319: o restaurante da URL (validado pela sessão) antes do guardado
        const storedSlug = activeSlug ?? getStoredTenantSlug()
        if (storedSlug && storedSlug !== 'platform') {
          const cfg = await config.get()
          // Montar um Tenant mínimo a partir do config
          setRestaurants([
            {
              id: cfg.tenant_id,
              slug: storedSlug,
              name: cfg.name,
              status: 'active',
              country_code: '',
              locale: cfg.primary_locale,
              custom_domain: null,
              created_at: cfg.created_at,
              updated_at: cfg.updated_at,
            },
          ])
        }
      }
    } catch {
      // Silencioso — sidebar degrada graciosamente
    } finally {
      setLoadingRestaurants(false)
    }
  }

  function toggleExpand(slug: string) {
    setExpandedSlugs((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) {
        next.delete(slug)
      } else {
        next.add(slug)
      }
      return next
    })
  }

  async function handleLogout() {
    // #319: o logout apaga também o slug guardado (antes ficava para a
    // próxima conta neste browser)
    await logoutSession()
    navigate('/login')
  }

  async function openCreateModal() {
    if (!handleLeaveResult(await confirmLeaveImpersonation(null, t))) return
    setCreateModalOpen(true)
  }

  function openStartModal(restaurant: Tenant, el?: HTMLElement | null) {
    setStartReturnFocus(el ?? null)
    setStartTarget({ id: restaurant.id, slug: restaurant.slug, name: restaurant.name })
  }

  function handleLeaveResult(result: 'ok' | 'cancelled' | 'stop_failed'): boolean {
    if (result === 'stop_failed') toast.error(t('admin.impersonation.end_err'))
    return result === 'ok'
  }

  /** Navega para a consola / outro restaurante com confirmação se houver sessão. */
  async function goWithSessionGuard(to: string, targetSlug: string | null) {
    if (!handleLeaveResult(await confirmLeaveImpersonation(targetSlug, t))) return
    navigate(to)
    setSidebarOpen(false)
  }

  async function onRestaurantNav(restaurant: Tenant, path: string, el: HTMLElement | null) {
    if (isPlatformOwner && needsImpersonationStart(restaurant.slug)) {
      // Sessão noutro restaurante → confirmar stop antes do modal
      if (!handleLeaveResult(await confirmLeaveImpersonation(restaurant.slug, t))) return
      openStartModal(restaurant, el)
      return
    }
    if (!handleLeaveResult(await confirmLeaveImpersonation(restaurant.slug, t))) return
    navigate(`/r/${restaurant.slug}/${path}`)
    setSidebarOpen(false)
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Toaster />
      {/* Live region persistente — montada vazia antes de qualquer anúncio */}
      <ImpersonationAnnouncer />
      {/* #430/#434: faixa sticky no fluxo; altura → --impersonation-banner-h */}
      <ImpersonationBanner />

      <div className="flex flex-1 min-h-0">
        {/* Mobile overlay — começa no fundo da faixa (sem tira clicável acima) */}
        {sidebarOpen && (
          <div
            className="fixed inset-x-0 bottom-0 z-40 bg-black/30 top-[var(--impersonation-banner-h,0px)] lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar — lg:rtl:translate-x-0: em ar, rtl:translate-x-full não vence o desktop */}
        <aside
          className={cn(
            'fixed bottom-0 start-0 z-50 flex flex-col bg-white border-e border-gray-200 transition-transform top-[var(--impersonation-banner-h,0px)] lg:top-0 lg:translate-x-0 lg:rtl:translate-x-0 lg:static lg:z-auto',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full',
            collapsed && 'lg:hidden',
          )}
          style={{ width: 280 }}
        >
          {/* Logo — PRATZU (arquivo oficial; o nome é a própria imagem, alt "PRATZU") */}
          <div className="flex items-center gap-2.5 px-5 h-16 border-b-2 border-pratzu-aubergine flex-shrink-0">
            <div className="min-w-0 flex-1">
              <PratzuLogo className="h-6 w-auto" />
              <p className="text-xs text-pratzu-plum mt-1 font-semibold">
                {t(isPlatformOwner ? 'admin.nav.platform' : 'admin.nav.section.operations')}
              </p>
            </div>
            {/* Recolher (desktop/tablet) — libera a tela toda para o quadro */}
            <button
              onClick={toggleCollapsed}
              title={t('admin.nav.collapse')}
              aria-label={t('admin.nav.collapse')}
              className="hidden lg:flex p-2 -mr-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 flex-shrink-0"
            >
              <CollapseIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Nav tree */}
          <nav className="flex-1 px-2 py-3 overflow-y-auto space-y-0.5">
            {loadingRestaurants ? (
              <div className="flex justify-center py-8">
                <Spinner className="w-5 h-5 text-indigo-400" />
              </div>
            ) : (
              restaurants.map((restaurant) => {
                const isExpanded = expandedSlugs.has(restaurant.slug)
                const isActiveRestaurant = activeSlug === restaurant.slug

                return (
                  <div key={restaurant.slug}>
                    {/* Restaurant header */}
                    <button
                      onClick={() => toggleExpand(restaurant.slug)}
                      className={cn(
                        'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left',
                        isActiveRestaurant
                          ? 'bg-indigo-50 text-indigo-800'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
                      )}
                    >
                      <BuildingIcon className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      <span className="flex-1 truncate">{restaurant.name}</span>
                      {isExpanded ? (
                        <ChevronDownIcon className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      )}
                    </button>

                    {/* Sub-items */}
                    {isExpanded && (
                      <div className="mt-0.5 space-y-0.5 pl-3">
                        {visibleSubItems(SUB_ITEMS, userRoles).map(
                          ({ path, labelKey, icon: Icon, testId }) => {
                            const to = `/r/${restaurant.slug}/${path}`
                            const isActive = location.pathname === to

                            return (
                              <button
                                key={path}
                                data-testid={testId}
                                onClick={(e) => {
                                  void onRestaurantNav(restaurant, path, e.currentTarget)
                                }}
                                className={cn(
                                  'flex items-center gap-2 w-full px-3 py-1.5 rounded-lg text-xs font-medium transition-colors text-left',
                                  isActive
                                    ? 'bg-indigo-100 text-indigo-700'
                                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                                )}
                              >
                                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                                {t(labelKey)}
                              </button>
                            )
                          },
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            )}

            {/* Links de plataforma — apenas platform_owner */}
            {isPlatformOwner && (
              <button
                onClick={() => {
                  void goWithSessionGuard('/platform-users', null)
                }}
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors mt-2',
                  location.pathname === '/platform-users'
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                )}
              >
                <UsersIcon className="w-4 h-4 flex-shrink-0" />
                {t('admin.nav.platform_users')}
              </button>
            )}
            {isPlatformOwner && (
              <button
                onClick={() => {
                  void goWithSessionGuard('/platform-ideas', null)
                }}
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  location.pathname === '/platform-ideas'
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                )}
              >
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10 2.5l1.4 3.1 3.4.4-2.5 2.3.7 3.3L10 10.2l-3 1.4.7-3.3-2.5-2.3 3.4-.4L10 2.5z"
                  />
                </svg>
                {t('admin.ideas.nav')}
              </button>
            )}
            {isPlatformOwner && (
              <button
                onClick={() => {
                  void goWithSessionGuard('/platform-leads', null)
                }}
                data-testid="nav-platform-leads"
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  location.pathname === '/platform-leads'
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                )}
              >
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4h12v9H8l-4 3V4z" />
                </svg>
                {t('admin.leads.nav')}
              </button>
            )}
            {isPlatformOwner && (
              <button
                onClick={() => {
                  void goWithSessionGuard('/platform-countries', null)
                }}
                data-testid="nav-platform-countries"
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  location.pathname === '/platform-countries'
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                )}
              >
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <circle cx="10" cy="10" r="7.5" />
                  <path
                    strokeLinecap="round"
                    d="M2.5 10h15M10 2.5c2.5 2.5 2.5 12.5 0 15M10 2.5c-2.5 2.5-2.5 12.5 0 15"
                  />
                </svg>
                {t('admin.countries.nav')}
              </button>
            )}
            {isPlatformOwner && (
              <button
                onClick={() => {
                  void goWithSessionGuard('/platform-contacts', null)
                }}
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  location.pathname === '/platform-contacts'
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                )}
              >
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l7 5 7-5" />
                </svg>
                {t('admin.contact.nav')}
              </button>
            )}
            {isPlatformOwner && (
              <button
                onClick={() => {
                  void goWithSessionGuard('/platform-translations', null)
                }}
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  location.pathname === '/platform-translations'
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                )}
              >
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <circle cx="10" cy="10" r="7.5" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 2.5v15M2.5 10h15" />
                </svg>
                {t('admin.nav.platform_translations')}
              </button>
            )}
            {isPlatformOwner && (
              <button
                onClick={() => {
                  void goWithSessionGuard('/platform-plans', null)
                }}
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  location.pathname === '/platform-plans'
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                )}
              >
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <rect x="2.5" y="5.5" width="15" height="9" rx="2" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 8.5h4M5 11.5h6" />
                </svg>
                {t('admin.nav.platform_plans')}
              </button>
            )}
          </nav>

          {/* Footer: idioma do painel + novo restaurante + logout */}
          <div className="px-2 py-3 border-t border-gray-100 space-y-0.5 flex-shrink-0">
            {localeOptions.length > 1 && (
              <div className="px-1 pb-2">
                <Select
                  aria-label={t('admin.nav.language')}
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                  options={localeOptions}
                  optionLang
                />
              </div>
            )}
            {isPlatformOwner && (
              <button
                onClick={() => void openCreateModal()}
                className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                {t('admin.tenant.new')}
              </button>
            )}
            <button
              onClick={handleLogout}
              data-testid="nav-logout"
              className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              <LogoutIcon className="w-4 h-4" />
              {t('admin.nav.logout')}
            </button>
          </div>
        </aside>

        {/* Reabrir barra lateral (desktop/tablet colapsada) */}
        {collapsed && (
          <button
            onClick={toggleCollapsed}
            title={t('admin.nav.expand')}
            aria-label={t('admin.nav.expand')}
            className="hidden lg:flex fixed top-[calc(0.75rem+var(--impersonation-banner-h,0px))] start-3 z-40 items-center justify-center w-10 h-10 rounded-xl bg-white border border-gray-200 shadow-md text-gray-500 hover:text-indigo-600 hover:border-indigo-300"
          >
            <ExpandIcon className="w-5 h-5" />
          </button>
        )}

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile header */}
          <header className="lg:hidden flex items-center h-14 px-4 bg-white border-b border-gray-200">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  d="M3 5h14M3 10h14M3 15h14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </header>

          {/* #229/#230: banner de localização (ADR-0014) — gestão apenas;
            nunca bloqueia o salão, é só um aviso com confirmação em 1 toque */}
          {userRoles.some(
            (r) => r === 'manager' || r === 'restaurant_owner' || r === 'platform_owner',
          ) && (
            <div className="px-4 pt-4 sm:px-6 lg:px-8">
              <LocationBanner />
            </div>
          )}
          <main key={activeSlug ?? 'root'} className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
            {isPlatformOwner && !activeSlug && (
              <div className="mb-4">
                <ExpiredSessionNotice
                  restaurants={restaurants.map((r) => ({
                    id: r.id,
                    slug: r.slug,
                    name: r.name,
                  }))}
                  onEnterAgain={(r) => {
                    const full = restaurants.find((x) => x.id === r.id || x.slug === r.slug)
                    if (full) openStartModal(full)
                  }}
                />
              </div>
            )}
            {children}
          </main>
        </div>
      </div>

      {/* Modal: criar restaurante (#418 — componente dedicado) */}
      <CreateTenantModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={loadRestaurants}
      />

      {/* #430 — entrar como plataforma */}
      <StartImpersonationModal
        open={!!startTarget}
        restaurant={startTarget}
        returnFocusEl={startReturnFocus}
        onClose={() => setStartTarget(null)}
        onStarted={(slug) => {
          navigate(`/r/${slug}/dashboard`)
          setSidebarOpen(false)
          focusPageH1()
        }}
      />
    </div>
  )
}

// ─── Ícones inline ────────────────────────────────────────────────────────────
// Os ícones do menu lateral vivem em nav.tsx (extraídos na #336 para o
// SUB_ITEMS poder ser testado sem montar o Shell).

/**
 * Marca PRATZU — arquivos oficiais em public/brand (identidade/logo).
 * "PRATZU" é nome próprio: o alt é igual em todas as línguas (fora do i18n).
 */
export function PratzuLogo({
  className,
  variant = 'horizontal',
}: {
  className?: string
  variant?: 'horizontal' | 'simbolo'
}) {
  const src =
    variant === 'simbolo' ? '/brand/pratzu-symbol-color.svg' : '/brand/pratzu-horizontal-color.svg'
  return <img className={className} src={src} alt="PRATZU" />
}
